import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { SpinnerGap, Buildings, MagnifyingGlass, Archive, HardDrives, CurrencyDollar, Warning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Pager } from "./AdminHelpers";

export function AdminAccountsSection() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [acctSearch, setAcctSearch] = useState("");
  const [acctPage, setAcctPage] = useState(1);
  const [acctPages, setAcctPages] = useState(1);

  const loadAccounts = useCallback(async (page = 1, search = "") => {
    setLoading(true);
    try { const resp = await api.get(`/admin/accounts?page=${page}&limit=15&search=${encodeURIComponent(search)}`); setAccounts(resp.data.accounts); setAcctPages(resp.data.pages); setAcctPage(page); }
    catch (e) { toast.error("Failed to load accounts"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  const updateAccount = async (accountId, changes) => {
    try { await api.patch(`/admin/accounts/${accountId}`, changes); toast.success("Account updated"); loadAccounts(acctPage, acctSearch); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading && accounts.length === 0) return <div className="flex justify-center py-8"><SpinnerGap size={24} className="animate-spin text-[#FF4400]" /></div>;

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input className="w-full pl-9 pr-3 py-2 bg-[var(--bg-base)] border border-[var(--border-strong)] rounded-lg text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[#FF4400]" placeholder="Search accounts..." value={acctSearch} onChange={(e) => setAcctSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadAccounts(1, acctSearch)} data-testid="admin-account-search" />
        </div>
        <Button onClick={() => loadAccounts(1, acctSearch)} className="bg-[#FF4400] hover:bg-[#FF6633] text-white text-xs">Search</Button>
      </div>
      <div className="space-y-1">
        {accounts.map((a) => (
          <div key={a.account_id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-[var(--bg-card)] border border-[var(--border)]" data-testid={`admin-acct-${a.account_id}`}>
            <Buildings size={16} className="text-[var(--text-muted)] shrink-0" />
            <div className="flex-1 min-w-0"><p className="text-sm font-medium text-[var(--text-primary)] truncate">{a.name}</p><p className="text-[10px] text-[var(--text-muted)]">{a.owner_email} &middot; {a.seat_used}/{a.seat_limit || 26} seats &middot; {a.business_count} biz</p></div>
            <div className="flex items-center gap-2 shrink-0">
              <select className="text-xs bg-transparent border border-[var(--border)] rounded px-1 py-0.5 text-[var(--text-secondary)]" value={a.plan_tier || "free_beta"} onChange={(e) => updateAccount(a.account_id, { plan_tier: e.target.value })} data-testid={`admin-tier-${a.account_id}`}>
                <option value="free_beta">Free Beta</option><option value="creator">Creator</option><option value="studio">Studio</option><option value="agency">Agency</option><option value="enterprise">Enterprise</option>
              </select>
              <select className="text-xs bg-transparent border border-[var(--border)] rounded px-1 py-0.5 text-[var(--text-secondary)]" value={a.status || "active"} onChange={(e) => updateAccount(a.account_id, { status: e.target.value })} data-testid={`admin-status-${a.account_id}`}>
                <option value="active">Active</option><option value="trialing">Trialing</option><option value="past_due">Past Due</option><option value="canceled">Canceled</option>
              </select>
            </div>
          </div>
        ))}
      </div>
      <Pager page={acctPage} pages={acctPages} onPage={(p) => loadAccounts(p, acctSearch)} />
    </div>
  );
}

export function AdminUsageSection() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [usageStats, setUsageStats] = useState(null);

  const loadUsage = useCallback(async () => {
    setLoading(true);
    try { const resp = await api.get("/admin/usage"); setUsageStats(resp.data); }
    catch (e) { toast.error("Failed to load usage"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { loadUsage(); }, [loadUsage]);

  if (loading || !usageStats) return <div className="flex justify-center py-8"><SpinnerGap size={24} className="animate-spin text-[#FF4400]" /></div>;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[{ label: "Total Users", val: usageStats.total_users }, { label: "Total Accounts", val: usageStats.total_accounts }, { label: "Total Businesses", val: usageStats.total_businesses }, { label: `Generations (${usageStats.month})`, val: usageStats.total_generations_month }, { label: "Generations (All Time)", val: usageStats.total_generations_all }, { label: "Templates", val: usageStats.total_templates }].map((s) => (
          <div key={s.label} className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-4 text-center"><p className="text-2xl font-bold text-[var(--text-primary)]">{s.val}</p><p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mt-1">{s.label}</p></div>
        ))}
      </div>
      {usageStats.top_users?.length > 0 && (
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-4">
          <h4 className="text-sm font-semibold text-[var(--text-primary)] mb-3">Top Users This Month</h4>
          <div className="space-y-1">{usageStats.top_users.map((u, i) => <div key={u.user_id} className="flex items-center gap-2 text-sm"><span className="w-5 text-center text-xs text-[var(--text-muted)]">{i + 1}.</span><span className="flex-1 text-[var(--text-secondary)] truncate">{u.name || u.email}</span><span className="font-medium text-[var(--text-primary)]">{u.generations}</span></div>)}</div>
        </div>
      )}
    </div>
  );
}

export function AdminAuditSection() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [auditEvents, setAuditEvents] = useState([]);
  const [auditPage, setAuditPage] = useState(1);
  const [auditPages, setAuditPages] = useState(1);
  const [auditAction, setAuditAction] = useState("");

  const loadAudit = useCallback(async (page = 1, action = "") => {
    setLoading(true);
    try { const resp = await api.get(`/admin/audit-log?page=${page}&limit=20&action=${action}`); setAuditEvents(resp.data.events); setAuditPages(resp.data.pages); setAuditPage(page); }
    catch (e) { toast.error("Failed to load audit log"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { loadAudit(); }, [loadAudit]);

  if (loading && auditEvents.length === 0) return <div className="flex justify-center py-8"><SpinnerGap size={24} className="animate-spin text-[#FF4400]" /></div>;

  return (
    <div className="space-y-3">
      <select className="bg-[var(--bg-base)] border border-[var(--border-strong)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)]" value={auditAction} onChange={(e) => { setAuditAction(e.target.value); loadAudit(1, e.target.value); }} data-testid="admin-audit-filter">
        <option value="">All Events</option>
        <option value="account_created">Account Created</option><option value="business_created">Business Created</option><option value="business_archived">Business Archived</option><option value="member_invited">Member Invited</option><option value="member_removed">Member Removed</option><option value="role_changed">Role Changed</option><option value="business_access_granted">Business Access Granted</option><option value="seat_limit_reached">Seat Limit Reached</option><option value="admin_force_logout">Admin: Force Logout</option><option value="admin_user_suspended">Admin: User Suspended</option><option value="admin_user_reactivated">Admin: User Reactivated</option><option value="admin_account_updated">Admin: Account Updated</option>
      </select>
      <div className="space-y-1">
        {auditEvents.map((evt) => (
          <div key={evt.event_id} className="px-3 py-2 rounded-lg bg-[var(--bg-card)] border border-[var(--border)] text-xs">
            <div className="flex items-center justify-between"><span className="font-medium text-[var(--text-primary)]">{evt.action.replace(/_/g, " ")}</span><span className="text-[var(--text-muted)]">{evt.created_at?.slice(0, 19).replace("T", " ")}</span></div>
            {evt.details && Object.keys(evt.details).length > 0 && <p className="text-[var(--text-muted)] mt-0.5">{Object.entries(evt.details).map(([k, v]) => `${k}: ${v}`).join(" · ")}</p>}
          </div>
        ))}
        {auditEvents.length === 0 && <p className="text-sm text-[var(--text-muted)] text-center py-6">No audit events found.</p>}
      </div>
      <Pager page={auditPage} pages={auditPages} onPage={(p) => loadAudit(p, auditAction)} />
    </div>
  );
}

export function AdminSystemStatsSection() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [systemStats, setSystemStats] = useState(null);

  const loadSystem = useCallback(async () => {
    setLoading(true);
    try { const resp = await api.get("/admin/system"); setSystemStats(resp.data); }
    catch (e) { toast.error("Failed to load system stats"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { loadSystem(); }, [loadSystem]);

  if (loading || !systemStats) return <div className="flex justify-center py-8"><SpinnerGap size={24} className="animate-spin text-[#FF4400]" /></div>;

  return (
    <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3"><div className="w-2 h-2 rounded-full bg-green-400"></div><h4 className="text-sm font-semibold text-[var(--text-primary)]">System Healthy</h4></div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {Object.entries(systemStats.collections).map(([name, count]) => (
          <div key={name} className="flex items-center justify-between px-2 py-1.5 rounded bg-[var(--bg-base)] text-xs"><span className="text-[var(--text-muted)] font-mono">{name}</span><span className="text-[var(--text-primary)] font-medium">{count}</span></div>
        ))}
      </div>
    </div>
  );
}

export function AdminStorageSection() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [storageData, setStorageData] = useState(null);

  const loadStorageData = useCallback(async () => {
    setLoading(true);
    try { const resp = await api.get("/admin/storage-by-workspace"); setStorageData(resp.data); }
    catch (e) { toast.error("Failed to load storage data"); }
    setLoading(false);
  }, [api]);

  useEffect(() => { loadStorageData(); }, [loadStorageData]);

  if (loading || !storageData) return <div className="flex justify-center py-8"><SpinnerGap size={24} className="animate-spin text-[#FF4400]" /></div>;

  return (
    <div className="space-y-4" data-testid="admin-storage-section">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[{ label: "Accounts w/ Files", value: storageData.summary.total_accounts_with_files, icon: Buildings }, { label: "Total Files", value: storageData.summary.total_files.toLocaleString(), icon: Archive }, { label: "Total Size", value: `${storageData.summary.total_size_mb} MB`, icon: HardDrives }, { label: "At Limit", value: storageData.summary.accounts_at_limit, icon: Warning, color: storageData.summary.accounts_at_limit > 0 ? "text-red-400" : "" }, { label: "Active Add-ons", value: storageData.summary.active_addon_subscriptions, icon: CurrencyDollar }].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-3" data-testid={`storage-summary-${label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex items-center gap-1.5 mb-1"><Icon size={13} className="text-[var(--text-muted)]" /><span className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-medium">{label}</span></div>
            <span className={`text-lg font-['JetBrains_Mono'] font-bold ${color || "text-[var(--text-primary)]"}`}>{value}</span>
          </div>
        ))}
      </div>
      {storageData.summary.accounts_near_limit > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20" data-testid="storage-near-limit-alert"><Warning size={16} className="text-amber-400 shrink-0" /><p className="text-xs text-amber-400">{storageData.summary.accounts_near_limit} account{storageData.summary.accounts_near_limit > 1 ? "s" : ""} at 80%+ storage usage — potential upsell candidates</p></div>
      )}
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl overflow-hidden" data-testid="storage-workspace-table">
        <div className="px-4 py-3 border-b border-[var(--border)]"><h3 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">Storage by Account (Ranked by Usage %)</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="border-b border-[var(--border)] text-[var(--text-muted)]"><th className="text-left px-4 py-2.5 font-medium">Account</th><th className="text-left px-4 py-2.5 font-medium">Tier</th><th className="text-right px-4 py-2.5 font-medium">Files</th><th className="text-right px-4 py-2.5 font-medium">Size</th><th className="text-right px-4 py-2.5 font-medium">Limit</th><th className="text-right px-4 py-2.5 font-medium">Add-ons</th><th className="px-4 py-2.5 font-medium">Usage</th><th className="text-right px-4 py-2.5 font-medium">Last Upload</th></tr></thead>
            <tbody>
              {storageData.workspaces.length === 0 && <tr><td colSpan={8} className="px-4 py-6 text-center text-[var(--text-muted)]">No uploaded files yet.</td></tr>}
              {storageData.workspaces.map((w) => {
                const barColor = w.pct_used >= 100 ? "bg-red-500" : w.pct_used >= 80 ? "bg-amber-500" : "bg-emerald-500";
                return (
                  <tr key={w.account_id} className="border-b border-[var(--border)]/50 hover:bg-[var(--bg-raised)]/50 transition-colors" data-testid={`storage-row-${w.account_id}`}>
                    <td className="px-4 py-2.5"><span className="font-['JetBrains_Mono'] text-[var(--text-primary)]">{w.owner_email}</span></td>
                    <td className="px-4 py-2.5"><span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${w.tier === "agency" ? "bg-purple-500/15 text-purple-400" : w.tier === "pro" ? "bg-blue-500/15 text-blue-400" : w.tier === "creator" ? "bg-emerald-500/15 text-emerald-400" : "bg-zinc-500/15 text-zinc-400"}`}>{w.tier}</span></td>
                    <td className="px-4 py-2.5 text-right font-['JetBrains_Mono'] text-[var(--text-primary)]">{w.resource_bank_files}</td>
                    <td className="px-4 py-2.5 text-right font-['JetBrains_Mono'] text-[var(--text-muted)]">{w.total_size_mb} MB</td>
                    <td className="px-4 py-2.5 text-right font-['JetBrains_Mono'] text-[var(--text-muted)]">{w.effective_limit === -1 ? "\u221E" : w.effective_limit}{w.addon_slots > 0 && <span className="text-[10px] text-[#FF4400] ml-1">(+{w.addon_slots})</span>}</td>
                    <td className="px-4 py-2.5 text-right font-['JetBrains_Mono']">{w.addon_count > 0 ? <span className="text-[#FF4400]">{w.addon_count}</span> : <span className="text-[var(--text-muted)]">—</span>}</td>
                    <td className="px-4 py-2.5"><div className="flex items-center gap-2"><div className="flex-1 h-1.5 bg-[var(--border)] rounded-full overflow-hidden" style={{minWidth: 60}}><div className={`h-full rounded-full ${barColor} transition-all`} style={{width: `${Math.min(w.pct_used, 100)}%`}} /></div><span className={`text-[10px] font-['JetBrains_Mono'] font-semibold w-10 text-right ${w.pct_used >= 100 ? "text-red-400" : w.pct_used >= 80 ? "text-amber-400" : "text-[var(--text-muted)]"}`}>{w.pct_used}%</span></div></td>
                    <td className="px-4 py-2.5 text-right text-[var(--text-muted)]">{w.latest_upload ? new Date(w.latest_upload).toLocaleDateString() : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
